/* ---------------------------------------------------------------------------
   Getting a model's geometry out of the modeller and into the bucket.

   The 3D view (src/map/scene3d.ts) wants the real structure, not a box at the
   recorded dimensions. That geometry only exists inside a running modeller — it is
   derived from parameters at boot — and the modeller's own `.glb` action puts a
   file in the user's downloads folder, which no other page can read. So this drives
   a modeller nobody sees, asks it to export, and PUTs the bytes at
   `/api/modeler/geometry`.

   THE SAME HIDDEN-FRAME ARRANGEMENT AS AN IMPORT, and for the same reasons — see
   the long note at the top of src/modeler/importModel.ts, which this deliberately
   mirrors rather than abstracts. Two runs, two purposes, ~40 lines of shared shape:
   a factored "hidden modeller session" would have to take the message names, the
   deadlines, the progress vocabulary and the teardown rule as parameters, which is
   more indirection than the duplication costs.

   One thing differs, and it is the reason this frame can be simpler: an export
   READS the model and changes nothing. No autosave to wait for, no flush on the way
   out, and no need to unmount the record's own viewport — two modellers on one
   model are only dangerous when one of them writes.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions, store } from '../core/store.ts';
import * as api from '../core/api.ts';
import { dropGeometry } from '../core/geometryCache.ts';

/** Booting the modeller is a 1.6 MB chunk plus the state fetch's own 8 s timeout. */
const BOOT_MS = 45_000;
/** Exporting is three.js walking the scene — seconds for a flyover, not minutes. */
const EXPORT_MS = 60_000;

interface FrameMessage {
  source?: string;
  type?: string;
  glb?: unknown;
  message?: string;
}

/**
 * Export one model's geometry and store it.
 *
 * Resolves when the bytes are in the bucket, or rejects with something fit to show
 * a user. The caller is the 3D view's "Build geometry" action, which is the only
 * place that knows whether anyone is waiting for it.
 */
export async function exportModelGeometry(bridgeId: string, modelId: string): Promise<number> {
  const frame = h('iframe', {
    // `embed=viewport` boots the modeller reduced to its canvas, on this model.
    // The scene has to exist to be exported, so this is the mode that builds it
    // with the least chrome around it.
    src: `modeler?${new URLSearchParams({
      bridge: bridgeId,
      model: modelId,
      mode: 'modeler',
      embed: 'viewport',
      theme: store.get().theme,
    })}`,
    title: 'Exporting a bridge model',
    'aria-hidden': 'true',
    tabindex: '-1',
    // As the import's frame: same-origin for the storage shim, no downloads and no
    // modals — a modal in a frame nobody can see is a run that never finishes.
    sandbox: 'allow-scripts allow-same-origin',
  });
  const stage = h('div', { class: 'mimp-stage', 'aria-hidden': 'true' }, frame);
  document.body.appendChild(stage);

  let waiting: { match: (m: FrameMessage) => boolean; settle: (m: FrameMessage | null) => void } | null =
    null;

  const onMessage = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin) return;
    if (event.source !== frame.contentWindow) return;
    const data = event.data as FrameMessage | null;
    if (data?.source !== 'bridge-modeler') return;
    if (waiting?.match(data)) {
      const settle = waiting.settle;
      waiting = null;
      settle(data);
    }
  };
  window.addEventListener('message', onMessage);

  const next = (match: (m: FrameMessage) => boolean, ms: number): Promise<FrameMessage | null> =>
    new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        if (waiting?.settle === settle) waiting = null;
        resolve(null);
      }, ms);
      const settle = (m: FrameMessage | null): void => {
        window.clearTimeout(timer);
        resolve(m);
      };
      waiting = { match, settle };
    });

  try {
    const ready = await next((m) => m.type === 'ready', BOOT_MS);
    if (!ready) throw new Error('The modeller did not start, so nothing was exported.');

    frame.contentWindow?.postMessage(
      { source: 'bridge-monitor', type: 'export-glb' },
      window.location.origin,
    );

    const done = await next(
      (m) => m.type === 'exported' || m.type === 'export-failed',
      EXPORT_MS,
    );
    if (!done) throw new Error('The export did not finish.');
    if (done.type === 'export-failed') {
      throw new Error(typeof done.message === 'string' ? done.message : 'The export failed.');
    }
    if (!(done.glb instanceof ArrayBuffer) || done.glb.byteLength === 0) {
      throw new Error('The modeller returned no geometry.');
    }

    const bytes = await api.putModelGeometry(bridgeId, modelId, done.glb);
    /* THE DEVICE'S CACHED COPY IS NOW CERTAINLY THE OLD ONE. src/core/geometryCache.ts
       serves the 3D views from IndexedDB before it asks the server anything, and this
       PUT has just replaced the object it holds — so without this the view that
       reopens a second later paints the geometry this export was made to replace, and
       only corrects itself once the background check comes back. Dropped rather than
       overwritten with the bytes in hand, because the PUT's answer does not carry the
       new ETag and a stored copy with no fingerprint cannot be revalidated. */
    await dropGeometry(bridgeId, modelId);
    return bytes;
  } finally {
    window.removeEventListener('message', onMessage);
    stage.remove();
  }
}

/**
 * The action behind the 3D view's "Build geometry" button.
 *
 * Reports through the store rather than by resolving, because the button is gone
 * from the screen by the time this finishes — the view redraws with the structure
 * on it — and because a failure has to be readable somewhere other than a console.
 */
export async function buildModelGeometry(bridgeId: string, modelId: string): Promise<void> {
  actions.geometryBuildStarted(bridgeId, modelId);
  try {
    const bytes = await exportModelGeometry(bridgeId, modelId);
    actions.geometryBuildFinished(null);
    // The index carries `hasGeometry`, and the 3D view reads it to decide whether
    // to draw the model or a box — so it has to be re-read, not assumed.
    await actions.refreshModels();
    console.info(`[geometry] ${bridgeId}/${modelId}: ${bytes} bytes`);
  } catch (err) {
    actions.geometryBuildFinished(
      err instanceof Error ? err.message : 'The geometry could not be exported.',
    );
  }
}
